import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  HostListener,
  AfterViewInit,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { VOneIconComponent, VOneInputComponent } from '@shared-components';
import { SelectOption } from '@shared-models';

@Component({
  selector: 'v-one-paginator',
  imports: [
    CommonModule,
    forwardRef(() => VOneIconComponent),
    forwardRef(() => VOneInputComponent),
    TranslatePipe,
  ],
  templateUrl: './v-one-paginator.component.html',
  styleUrls: ['./v-one-paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VOnePaginatorComponent implements AfterViewInit {
  totalItems = input.required<number>();
  pageIndexBase = input<0 | 1>(1);
  currentPage = input<number>(1);
  itemsPerPage = input<number>(8);
  variant = input<'basic' | 'advanced'>('basic');
  pageSizeOptions = input<number[]>([8, 16, 24, 32, 48]);
  maxVisiblePages = input<number>(5);
  className = input<string>('');

  pageChange = output<number>();
  itemsPerPageChange = output<number>();

  readonly selectedPageSize = signal<number>(8);
  private readonly isMobile = signal<boolean>(false);

  readonly advancedPagesCount = computed(() => {
    return this.isMobile() ? 3 : 5;
  });

  readonly totalPages = computed(() => {
    const total = this.totalItems();
    const perPage = this.itemsPerPage();
    return Math.ceil(total / perPage);
  });

  readonly isFirstPage = computed(() => {
    return this.currentPage() === 1;
  });

  readonly isLastPage = computed(() => {
    return this.currentPage() >= this.totalPages();
  });

  readonly visiblePages = computed(() => {
    const current = this.currentPage();
    const total = this.totalPages();
    const variant = this.variant();
    const maxVisible = this.maxVisiblePages();

    if (variant === 'basic') {
      return this.getBasicPages(current, total, maxVisible);
    } else {
      const advancedVisible = this.advancedPagesCount();
      return this.getAdvancedPages(current, total, advancedVisible);
    }
  });

  readonly paginatorClasses = computed(() => {
    const baseClass = 'v-one-paginator';
    const variantClass = `${baseClass}--${this.variant()}`;
    const customClass = this.className();

    return [baseClass, variantClass, customClass].filter(Boolean).join(' ');
  });

  readonly pageSizeSelectOptions = computed<SelectOption[]>(() => {
    return this.pageSizeOptions().map((size) => ({
      value: size.toString(),
      label: size.toString(),
      disabled: false,
    }));
  });

  ngAfterViewInit() {
    this.checkScreenSize();
    this.selectedPageSize.set(this.itemsPerPage());
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth <= 575);
    }
  }

  private getBasicPages(
    current: number,
    total: number,
    maxVisible: number,
  ): (number | string)[] {
    const pages: number[] = [];
    const half = Math.floor(maxVisible / 2);

    let start = Math.max(1, current - half);
    const end = Math.min(total, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  private getAdvancedPages(
    current: number,
    total: number,
    visibleCount: number,
  ): (number | string)[] {
    const minPagesForEllipsis = visibleCount + 4;
    if (total <= minPagesForEllipsis) {
      return [...Array(total).keys()].map((index) => index + 1);
    }

    const pages: (number | string)[] = [1];
    const halfVisible = Math.floor(visibleCount / 2);

    let start: number;
    let end: number;

    if (current <= halfVisible + 2) {
      start = 2;
      end = visibleCount + 1;
    } else if (current >= total - halfVisible - 1) {
      start = total - visibleCount;
      end = total - 1;
    } else {
      start = current - halfVisible;
      end = current + halfVisible;
    }

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total) {
        pages.push(i);
      }
    }

    if (end < total - 1) {
      pages.push('...');
    }

    if (total > 1) {
      pages.push(total);
    }

    return pages;
  }

  onPageClick(page: number | string): void {
    if (typeof page === 'string') return;
    if (page === this.currentPage()) return;
    if (page < 1 || page > this.totalPages()) return;

    const emitPage = this.pageIndexBase() === 0 ? page - 1 : page;
    this.pageChange.emit(emitPage);
  }

  onPreviousClick(): void {
    if (this.isFirstPage()) return;
    const newPage = this.currentPage() - 1;

    const emitPage = this.pageIndexBase() === 0 ? newPage - 1 : newPage;
    this.pageChange.emit(emitPage);
  }

  onNextClick(): void {
    if (this.isLastPage()) return;
    const newPage = this.currentPage() + 1;

    const emitPage = this.pageIndexBase() === 0 ? newPage - 1 : newPage;
    this.pageChange.emit(emitPage);
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const newSize = parseInt(target.value, 10);

    if (isNaN(newSize)) return;

    this.selectedPageSize.set(newSize);
    this.itemsPerPageChange.emit(newSize);

    if (this.currentPage() !== 1) {
      const emitPage = this.pageIndexBase() === 0 ? 0 : 1;
      this.pageChange.emit(emitPage);
    }
  }

  onPageSizeSelectChange(value: string | number): void {
    const newSize = typeof value === 'string' ? parseInt(value, 10) : value;

    if (isNaN(newSize)) return;

    this.selectedPageSize.set(newSize);
    this.itemsPerPageChange.emit(newSize);

    if (this.currentPage() !== 1) {
      const emitPage = this.pageIndexBase() === 0 ? 0 : 1;
      this.pageChange.emit(emitPage);
    }
  }

  isPageActive(page: number | string): boolean {
    return typeof page === 'number' && page === this.currentPage();
  }

  isEllipsis(page: number | string): boolean {
    return typeof page === 'string';
  }
}
